import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import Header from './Header';
import { apiService } from '../services/api';

export type CalendarMeta = {
  id: number;
  slug: string;
  title: string;
  subtitle?: string | null;
  disease_area?: string | null;
  line_of_therapy?: string | null;
  indication_id?: number | null;
  indication_name?: string | null;
  version_label?: string | null;
  version_number?: number;
  created_at?: string | null;
};

export type CalendarRow = {
  id: number;
  sponsor?: string | null;
  trial_name?: string | null;
  regimen?: string | null;
  indication_text?: string | null;
  phase?: string | null;
  est_primary_completion?: string | null;
  planned_n?: string | null;
  nct_id?: string | null;
  trial_id?: number | null;
  tier?: string | null;
  data_status?: string | null;
  notes?: string | null;
};

export type CalendarCatalogItem = {
  id: number;
  slug: string;
  title: string;
  disease_area?: string | null;
  line_of_therapy?: string | null;
  indication_id?: number | null;
  indication_name?: string | null;
  row_count?: number;
  version_label?: string | null;
};

function diseaseKey(value?: string | null): string {
  return (value || '').trim().toLowerCase().replace(/\s+/g, '-');
}

function normalizeText(value?: string | null): string {
  return (value || '')
    .toLowerCase()
    .replace(/ras[-\s]?mutant/g, 'ras mutant')
    .replace(/[^a-z0-9+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stageKey(line?: string | null): string {
  const t = (line || '').toUpperCase().replace(/\s+/g, ' ');
  if (!t) return '';
  if (/\bNEOADJUVANT\b|\bPERIOPERATIVE\b/.test(t)) return 'neoadjuvant';
  if (/\bADJUVANT\b/.test(t)) return 'adjuvant';
  const match = t.match(/(\d+)\s*L\s*(\+)?/);
  if (!match) return '';
  return `${match[1]}L${match[2] || ''}`;
}

function stagesCompatible(a: string, b: string): boolean {
  if (!a || !b) return !a && !b;
  if (a === b) return true;
  // Treat 2L and 2L+ as the same line family when matching calendars.
  return a.replace(/\+/g, '') === b.replace(/\+/g, '');
}

/**
 * Score how well a calendar matches a landscape disease/line.
 * Returns 0 when the calendar should not be shown for that line.
 */
export function scoreCalendarMatch(
  calendar: {
    disease_area?: string | null;
    line_of_therapy?: string | null;
    indication_id?: number | null;
    indication_name?: string | null;
    title?: string | null;
    slug?: string | null;
  },
  landscape: {
    disease_area?: string | null;
    line_of_therapy?: string | null;
    indication_id?: number | null;
    indication_name?: string | null;
    title?: string | null;
  },
): number {
  let score = 0;

  if (
    calendar.indication_id != null &&
    landscape.indication_id != null &&
    Number(calendar.indication_id) === Number(landscape.indication_id)
  ) {
    score += 100;
  }

  const calStage = stageKey(calendar.line_of_therapy);
  const landStage = stageKey(landscape.line_of_therapy);
  if (calStage && landStage && !stagesCompatible(calStage, landStage)) {
    return 0;
  }
  if (calStage && landStage && stagesCompatible(calStage, landStage)) {
    score += calStage === landStage ? 30 : 24;
  }

  const calDisease = normalizeText(calendar.disease_area);
  const landDisease = normalizeText(landscape.disease_area);
  const haystack = normalizeText(
    [landscape.disease_area, landscape.line_of_therapy, landscape.indication_name, landscape.title]
      .filter(Boolean)
      .join(' '),
  );
  const calHaystack = normalizeText(
    [calendar.disease_area, calendar.line_of_therapy, calendar.indication_name, calendar.title, calendar.slug]
      .filter(Boolean)
      .join(' '),
  );

  if (calDisease && landDisease) {
    if (calDisease === landDisease) score += 40;
    else if (calDisease.includes(landDisease) || landDisease.includes(calDisease)) score += 28;
  }
  if (calDisease && haystack.includes(calDisease)) score += 20;

  // If the calendar is scoped to a subtype (e.g. RAS-mutant NSCLC under NSCLC),
  // require those subtype tokens in the landscape line/indication.
  if (calDisease && landDisease && calDisease !== landDisease && calDisease.includes(landDisease)) {
    const parentTokens = new Set(landDisease.split(' ').filter(Boolean));
    const extras = calDisease
      .split(' ')
      .filter((t) => t.length > 2 && !parentTokens.has(t) && !['cancer', 'carcinoma', 'tumor'].includes(t));
    if (extras.length > 0) {
      const hits = extras.filter((t) => haystack.includes(t)).length;
      if (hits === 0) return 0;
      score += hits * 12;
    }
  }

  // Soft boost when landscape line tokens appear in calendar metadata.
  const landLine = normalizeText(landscape.line_of_therapy);
  if (landLine && calHaystack.includes(landLine)) score += 15;

  return score;
}

export function pickMatchingCalendar<T extends CalendarCatalogItem>(
  calendars: T[],
  landscape: {
    disease_area?: string | null;
    line_of_therapy?: string | null;
    indication_id?: number | null;
    indication_name?: string | null;
    title?: string | null;
  },
): T | null {
  let best: T | null = null;
  let bestScore = 0;
  for (const cal of calendars) {
    const score = scoreCalendarMatch(cal, landscape);
    if (score > bestScore) {
      best = cal;
      bestScore = score;
    }
  }
  // Require a minimum confidence so unrelated disease calendars don't attach.
  return bestScore >= 40 ? best : null;
}

function CalendarTable({
  rows,
  loading,
  error,
}: {
  rows: CalendarRow[];
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-12 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-8 text-rose-600 font-medium">{error}</div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-card overflow-x-auto">
      <table className="min-w-max w-full text-xs border-separate border-spacing-0">
        <thead className="sticky top-0 z-10 bg-slate-800 text-white">
          <tr>
            {[
              'Sponsor',
              'Trial name',
              'Regimen / comparison',
              'Indication',
              'Phase',
              'Key dates',
              'Planned N',
              'NCT#',
              'Tier',
              'Data status',
            ].map((label) => (
              <th
                key={label}
                className="px-3 py-2 text-left font-bold border border-slate-700 whitespace-nowrap"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={10} className="px-4 py-8 text-center text-slate-500">
                No rows match.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id} className="align-top hover:bg-slate-50/80">
                <td className="px-3 py-2 border border-slate-200 text-slate-700 min-w-[140px]">
                  {row.sponsor || '—'}
                </td>
                <td className="px-3 py-2 border border-slate-200 font-semibold text-slate-900 min-w-[160px]">
                  {row.trial_name || '—'}
                </td>
                <td className="px-3 py-2 border border-slate-200 text-slate-700 min-w-[220px]">
                  {row.regimen || '—'}
                </td>
                <td className="px-3 py-2 border border-slate-200 text-slate-700 min-w-[160px]">
                  {row.indication_text || '—'}
                </td>
                <td className="px-3 py-2 border border-slate-200 text-slate-700 whitespace-nowrap">
                  {row.phase || '—'}
                </td>
                <td className="px-3 py-2 border border-slate-200 text-slate-700 min-w-[200px]">
                  {row.est_primary_completion || '—'}
                </td>
                <td className="px-3 py-2 border border-slate-200 text-slate-700 whitespace-nowrap">
                  {row.planned_n || '—'}
                </td>
                <td className="px-3 py-2 border border-slate-200 whitespace-nowrap">
                  {row.nct_id ? (
                    row.trial_id ? (
                      <Link
                        to={`/trials/${row.trial_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-0.5 text-indigo-600 font-semibold hover:underline"
                      >
                        {row.nct_id}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    ) : (
                      <a
                        href={`https://clinicaltrials.gov/study/${row.nct_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-0.5 text-indigo-600 font-semibold hover:underline"
                      >
                        {row.nct_id}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 border border-slate-200 text-slate-700 min-w-[120px]">
                  {row.tier || '—'}
                </td>
                <td className="px-3 py-2 border border-slate-200 text-slate-700 min-w-[110px]">
                  {row.data_status || '—'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

type EmbeddedProps = {
  embedded: true;
  slug: string | null;
  emptyMessage?: string;
};

type StandaloneDetailProps = {
  embedded?: false;
};

export const CatalystCalendarPanel: React.FC<
  (EmbeddedProps | StandaloneDetailProps) & { slugOverride?: string }
> = (props) => {
  const embedded = Boolean(props.embedded);
  const params = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const slug = embedded
    ? (props as EmbeddedProps).slug
    : props.slugOverride || params.slug;

  const [calendar, setCalendar] = useState<CalendarMeta | null>(null);
  const [rows, setRows] = useState<CalendarRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setQuery('');
  }, [slug]);

  useEffect(() => {
    if (!slug) {
      setCalendar(null);
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.fetchEmergingCalendar(slug);
        if (cancelled) return;
        if (!response?.success) {
          setError(response?.error || 'Failed to load calendar');
          return;
        }
        setCalendar(response.calendar);
        setRows(response.rows || []);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load calendar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [
        r.sponsor,
        r.trial_name,
        r.regimen,
        r.indication_text,
        r.phase,
        r.nct_id,
        r.est_primary_completion,
        r.tier,
        r.data_status,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [rows, query]);

  if (embedded && !slug) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-8 text-slate-500 italic">
        {('emptyMessage' in props && props.emptyMessage) ||
          'No catalyst calendar uploaded for this disease line yet.'}
      </div>
    );
  }

  const searchBox = (
    <input
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      placeholder="Search sponsor, trial, NCT, phase…"
      className="w-full max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm"
    />
  );

  const table = <CalendarTable rows={filtered} loading={loading} error={error} />;

  if (embedded) {
    return (
      <div className="space-y-3">
        <div className="bg-white rounded-xl border border-gray-100 shadow-card px-4 py-3">
          <div className="text-sm font-semibold text-slate-800">
            {calendar?.title || slug}
            {rows.length > 0 ? (
              <span className="ml-2 text-xs font-medium text-slate-500">{rows.length} trials</span>
            ) : null}
          </div>
          {calendar?.subtitle && <p className="text-xs text-slate-500 mt-1">{calendar.subtitle}</p>}
          <div className="mt-3">{searchBox}</div>
        </div>
        {table}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <Header />
      <main className="max-w-[100%] mx-auto px-3 sm:px-4 py-4 space-y-3">
        <div className="bg-white rounded-xl border border-gray-100 shadow-card px-4 py-3">
          <button
            type="button"
            onClick={() => navigate('/emerging-calendars')}
            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 mb-1"
          >
            ← All catalyst calendars
          </button>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-lg font-bold text-slate-900 leading-snug">{calendar?.title || slug}</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {[
                  calendar?.disease_area,
                  calendar?.line_of_therapy,
                  calendar?.indication_name,
                  calendar?.version_label
                    ? `${calendar.version_label} · v${calendar.version_number}`
                    : calendar?.version_number
                      ? `v${calendar.version_number}`
                      : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {calendar?.subtitle && (
                <p className="text-xs text-slate-500 mt-1 max-w-3xl">{calendar.subtitle}</p>
              )}
            </div>
          </div>
          <div className="mt-3">{searchBox}</div>
        </div>
        {table}
      </main>
    </div>
  );
};

const CatalystCalendarsCatalog: React.FC = () => {
  const [items, setItems] = useState<CalendarCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await apiService.fetchEmergingCalendars();
        if (!cancelled) {
          if (response?.success) setItems(response.data || []);
          else setError(response?.error || 'Failed to load calendars');
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load calendars');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, { key: string; label: string; items: CalendarCatalogItem[] }>();
    for (const item of items) {
      const key = diseaseKey(item.disease_area) || item.slug;
      const label = (item.disease_area || item.slug).trim() || item.slug;
      if (!map.has(key)) map.set(key, { key, label, items: [] });
      map.get(key)!.items.push(item);
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-card px-5 py-4">
          <h1 className="text-xl font-bold text-slate-900">Catalyst Calendars</h1>
          <p className="text-sm text-slate-500 mt-1">
            Trial catalysts by disease and line — also available from Emerging Therapies via the Calendar
            toggle.
          </p>
        </div>
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : error ? (
          <p className="text-rose-600 font-medium">{error}</p>
        ) : groups.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-slate-500 italic">
            No catalyst calendars published yet.
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.key} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-sm font-bold text-slate-800">
                  {group.label}
                </div>
                <div className="divide-y divide-slate-100">
                  {group.items.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/emerging-calendars/${encodeURIComponent(item.slug)}`}
                      className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-indigo-50/40 transition-colors"
                    >
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{item.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {[item.line_of_therapy, item.indication_name, item.version_label]
                            .filter(Boolean)
                            .join(' · ')}
                          {item.row_count != null ? ` · ${item.row_count} trials` : ''}
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-indigo-600">Open →</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const CatalystCalendarsView: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  if (slug) return <CatalystCalendarPanel />;
  return <CatalystCalendarsCatalog />;
};

export default CatalystCalendarsView;
